import React, {
  ChangeEvent,
  useCallback,
  ReactElement,
  MouseEvent,
  KeyboardEvent,
  useRef,
  FormEvent,
  ElementType,
} from "react";
import {
  Column,
  TextInput,
  Button,
  InputAdornment,
  PageSpinner,
  Typography,
  TButtonProps,
} from "./../../../../components";
import { CheckmarkSmallIcon } from "./../../../../icons";
import classNames from "classnames";
import { ITextInputProps } from "./../TextInput/TextInput.interface";

interface IValidationInput {
  onChange: (code: string) => void;
  pattern?: RegExp;
  value: string;
  isLoading?: boolean;
  hasInitLoadDone?: boolean;
  errorMessage?: string | ReactElement;
  labels?: {
    inputLabel?: string;
    inputPlaceholder?: string;
    bttnText?: string;
    description?: string;
    title?: string;
  };
  isBttnDisabled?: boolean;
  textProps?: ITextInputProps;
  bttnProps?: TButtonProps;
  onBttnClick?: (e: MouseEvent) => void;
  onEnter?: (e: KeyboardEvent) => void;
  classes?: { root?: string; bttn?: string };
  containerProps?: {
    key: string;
    onSubmit(e: FormEvent): void;
  };
  containerType?: ElementType;
}

const ValidationInput = ({
  onChange: handleInput,
  pattern,
  value,
  isLoading = false,
  hasInitLoadDone = true,
  errorMessage = "",
  labels = {
    inputLabel: `Enter Zip code`,
    inputPlaceholder: `Enter Zip code`,
    bttnText: `Continue`,
    description: "",
    title: "",
  },
  isBttnDisabled = false,
  textProps = {},
  bttnProps = {},
  onBttnClick,
  onEnter,
  classes,
  containerType = "div",
  containerProps,
}: IValidationInput) => {
  const bttn = useRef(null);
  const endAdornment = useCallback(
    (injectedProps: any) => {
      const isSuccess = !errorMessage;
      const Icon = isSuccess ? (
        <CheckmarkSmallIcon
          className={classNames({
            "text-color-success": isSuccess,
          })}
          style={{
            width: `1em`,
            height: `1em`,
          }}
        />
      ) : null;

      return (
        <InputAdornment {...injectedProps} position="end">
          {isLoading ? <PageSpinner type="button" /> : Icon}
        </InputAdornment>
      );
    },
    [isLoading, errorMessage],
  );

  const onChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const val = event.target.value;
      if (!val.match(pattern ? new RegExp(pattern) : /^\d{0,5}$/g)) return;

      handleInput(val);
    },
    [handleInput, pattern],
  );

  const handleEnter = useCallback(
    (e: KeyboardEvent) => {
      bttn.current?.focus();

      onEnter?.(e);
    },
    [onEnter],
  );

  return (
    <Column
      {...containerProps}
      key="zip"
      xs={12}
      classes={{ root: classNames(classes?.root ?? ``) }}
      as={containerType}
    >
      {labels?.title && (
        <Typography
          as="p"
          variant="text-regular"
          classes={{ root: "text-color-grey-700" }}
          style={{ marginBottom: "32px" }}
        >
          {labels.title}
        </Typography>
      )}
      <TextInput
        label={labels.inputLabel ?? ``}
        errorMessage={errorMessage}
        placeholder={labels.inputPlaceholder ?? ``}
        value={value}
        onChange={onChange}
        disabled={isLoading}
        validity={errorMessage ? "error" : "none"}
        endAdornment={!hasInitLoadDone && !isLoading ? undefined : endAdornment}
        onEnter={handleEnter}
        {...textProps}
      />
      {labels?.description && (
        <Typography as="div" classes={{ root: "mt-6" }} variant="text-medium">
          {labels.description}
        </Typography>
      )}
      <Button
        ref={bttn}
        disabled={isBttnDisabled || isLoading || !!errorMessage}
        as="button"
        classes={{ root: classes?.bttn ?? `` }}
        onClick={onBttnClick}
        {...bttnProps}
      >
        {labels.bttnText ?? ``}
      </Button>
    </Column>
  );
};

export default ValidationInput;
